import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Box, Grid, Button, IconButton, Typography, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import * as HrActions from '../../../../../store/actions/hr.action';
import { getHrState } from '../../../../../store/selector/hr.selector';
import Services from '../../../../services';
import { columns } from './download-modal-columns';
import DynamicDataGrid from '../../datagrid/datagrid';
import { IDownloadModal } from '../../../../interfaces/modal.interface';

const DownloadModal = ({ onClose, options }: { onClose: () => void; options: IDownloadModal }) => {
  /* eslint-disable camelcase */
  const dispatch = useDispatch();

  const { reports_query } = useSelector(getHrState);

  const { approval_list } = useSelector(getHrState);
  const { dashboard_list } = useSelector(getHrState);

  const {
    is_loading_dashboard_list,
    is_loading_approval_list,
    dashboard_list: {
      ApplicationsReport: { applications },
    },
    approval_list: {
      ApprovalsReport: { approvals },
    },
  } = useSelector(getHrState);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const downloadData = async () => {
    const { data, success, errors } = await Services.api.hr.applicationDashboardExport({
      ...reports_query,
      is_export: 'application',
    });
    setIsDownloading(true);

    if (success) {
      window.open(Services.api.hr.downloadApplicationDashboardData(data), '_blank');
      Services.toast('success', 'File successfully exported.');
      setIsDownloading(false);
    } else {
      const [{ message }] = errors;
      Services.toast('error', message);
      setIsDownloading(false);
    }
  };

  const onPaginationRequest = (page: number) => {
    const q = { ...reports_query, page: page + 1 };

    dispatch(HrActions.DashboardApprovalData(q));
  };

  return (
    <Modal open={options.open} sx={{ bgcolor: '#00000050' }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { sm: 800, xs: '100%' },
          bgcolor: 'background.paper',
          borderRadius: '1rem',
          boxShadow: 24,
          p: '2rem',
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: '2rem',
            }}
          >
            <Typography sx={{ fontSize: '1.75rem' }}>{options.heading}</Typography>

            <IconButton
              color='primary'
              onClick={() => {
                downloadData();
              }}
            >
              {isDownloading ? (
                <CircularProgress color='primary' size={15} thickness={5} />
              ) : (
                <FileDownloadIcon />
              )}
            </IconButton>
          </Grid>

          <Grid item sx={{ background: '#fff', height: '60vh', width: '100%', mb: '2rem' }}>
            {(options.query === 'applications'
              ? !is_loading_dashboard_list
              : !is_loading_approval_list) &&
            (options.query === 'applications' ? dashboard_list.pages : approval_list.pages) ? (
              <DynamicDataGrid
                onPagination={(page: number) => onPaginationRequest(page)}
                columns={columns}
                rows={options.query === 'applications' ? applications : approvals}
                config={{
                  count:
                    options.query === 'applications' ? dashboard_list.count : approval_list.count,
                  currentPage:
                    options.query === 'applications'
                      ? dashboard_list.current_page
                      : approval_list.current_page,
                  pages:
                    options.query === 'applications' ? dashboard_list.pages : approval_list.pages,
                  total:
                    options.query === 'applications' ? dashboard_list.total : approval_list.total,
                }}
              />
            ) : (options.query === 'applications' && dashboard_list.total === 0) ||
              (options.query === 'approvals' && approval_list.total === 0) ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ mt: '1rem' }}>0 Data Found</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress color='primary' size={40} thickness={5} />

                <Typography sx={{ mt: '1rem' }}>Loading Data</Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              sx={{ p: '0.5rem 3rem', borderRadius: '1rem' }}
              variant='contained'
              onClick={() => {
                onClose();
                dispatch(
                  HrActions.DashboardApplicationData({
                    ...reports_query,
                    department: '',
                    month: '',
                    province: '',
                    reason: '',
                    signatory: '',
                    status: '',
                    year: '',
                  }),
                );
              }}
              disableElevation
            >
              Close
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default DownloadModal;
